import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";
import { useAuth, useCan } from "../auth/AuthProvider";
import { DatabaseBadge } from "../components/DatabaseBadge";
import { Icon } from "../components/icons";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  LoadingBlock,
  Mono,
  PageHeader,
  buttonStyles,
} from "../components/ui";
import { QuotaMeter } from "../components/Quota";
import { RegionTag, spansRegions } from "../components/RegionTag";
import { describeEngine } from "../lib/engines";
import { useDatabaseLifecycle, useDatabases } from "../lib/queries";

/** How many to fetch at a time, and how many more each "Show more". */
const PAGE = 25;

export function DatabasesPage() {
  const { t } = useTranslation("databases");
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id;
  const canManage = useCan("developer");

  const [limit, setLimit] = useState(PAGE);
  const databases = useDatabases(orgId, limit);
  const total = databases.data?.total ?? 0;
  const lifecycle = useDatabaseLifecycle(orgId ?? "");

  /*
   * Carried here by whichever page just finished: the create page announces a
   * new database, and the detail page's delete announces apps left holding a
   * variable that now points at nothing.
   */
  const location = useLocation();
  const notice = (location.state as { notice?: string } | null)?.notice ?? null;

  const rows = databases.data?.data ?? [];
  // Region on each row only once there is more than one to tell apart.
  const regioned = spansRegions(rows);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("title")}
        description={t("description")}
        action={
          canManage ? (
            <div className="flex flex-col items-end gap-1.5">
              <Link to="/databases/new" className={buttonStyles("inverse")}>
                <Icon name="plus" className="size-4" />
                {t("create")}
              </Link>
              <QuotaMeter orgId={currentOrg?.id} resource="databases" />
            </div>
          ) : undefined
        }
      />

      {/* Start and stop are the only mutations left on this page; deletion and
          everything else moved to the detail route. */}
      {lifecycle.error && <Alert>{lifecycle.error.message}</Alert>}
      {notice && <Alert tone="warning">{notice}</Alert>}

      {databases.isLoading ? (
        <LoadingBlock label={t("loading")} />
      ) : databases.isError ? (
        <Card>
          <EmptyState
            icon="database"
            title={t("loadFailed.title")}
            description={t("loadFailed.description")}
            action={
              <Button variant="secondary" size="sm" onClick={() => void databases.refetch()}>
                {t("loadFailed.retry")}
              </Button>
            }
          />
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState
            icon="database"
            title={t("empty.title")}
            description={t("empty.description")}
            action={
              canManage ? (
                <Link to="/databases/new" className={buttonStyles("inverse")}>
                  <Icon name="plus" className="size-4" />
                  {t("empty.create")}
                </Link>
              ) : undefined
            }
          />
        </Card>
      ) : (
        <Card>
          <ul className="divide-y divide-line">
            {rows.map((database) => {
              const state = database.status.value;
              // Lists intentionally carry the stored state to avoid an engine
              // call per row. The badge labels that fact; the detail route is
              // where a state-sensitive action can be confirmed first.
              const actionsDisabled = state === "creating";

              return (
              <li key={database.id} className="flex flex-wrap items-center gap-3 px-5 py-4 sm:gap-4">
                {/*
                  The row is the link, so the whole thing is one target rather
                  than a name to aim at. Six per-row buttons used to live here,
                  each opening a modal; they are the detail page's tabs now.
                */}
                <Link
                  to={`/databases/${database.id}`}
                  className="flex min-w-0 flex-1 items-center gap-4 max-sm:basis-full"
                >
                  <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-raised text-fg-subtle">
                    <Icon name="database" className="size-4" />
                  </span>

                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-fg">
                      {database.name}
                    </p>
                    <p className="ident truncate">
                      {describeEngine(database.engine)}
                      {database.internalHost && (
                        <>
                          {" · "}
                          <Mono>
                            {database.internalHost}:{database.port}
                          </Mono>
                        </>
                      )}
                    </p>
                  </div>
                </Link>

                {/* Our recorded state: the list does not ask the engine, since
                    that would be two calls per row on every page load. The
                    detail page resolves it properly, and the two can disagree —
                    that disagreement is usually the thing worth seeing. */}
                <div className="ml-auto flex flex-wrap items-center gap-1">
                  {regioned && (
                    <span className="mr-2">
                      <RegionTag region={database.region} />
                    </span>
                  )}
                  <DatabaseBadge state={state} stale={database.status.stale} />

                  {canManage && (
                    <>
                      <Button
                      variant="ghost"
                      size="sm"
                      disabled={actionsDisabled}
                      loading={lifecycle.isPending && lifecycle.variables?.id === database.id}
                      onClick={() =>
                        lifecycle.mutate({
                          id: database.id,
                          action: state === "running" ? "stop" : "start",
                        })
                      }
                    >
                      {state === "running" ? t("stop") : t("start")}
                    </Button>
                      <Link
                        to={`/databases/${database.id}`}
                        className={buttonStyles("secondary", "sm")}
                      >
                        {t("manage")}
                      </Link>
                    </>
                  )}
                </div>
              </li>
              );
            })}
          </ul>

          {total > rows.length && (
            <div className="border-t border-line p-3">
              <Button
                variant="secondary"
                size="sm"
                className="w-full"
                loading={databases.isPlaceholderData}
                onClick={() => setLimit((current) => current + PAGE)}
              >
                {t("more", { count: total - rows.length })}
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Shown for any database rather than only a running one: stopping no
          longer stops the bill, which is exactly what somebody about to click
          Stop to save money needs to have read first. */}
      {rows.length > 0 && (
        <p className="text-xs text-fg-subtle">{t("billing")}</p>
      )}

    </div>
  );
}
